import { Loader2, Sparkles } from "lucide-react";
import { useState } from "react";

import { getActiveWorkspaceId } from "../workspace/activeWorkspace";
import { invokeAi } from "../ai/invokeAi";

/**
 * AI campaign assistance: subject lines, audience ideas, copy guidance and
 * a follow-up recommendation. Suggestions ONLY; nothing here writes to the
 * campaign or sends anything. The reads run with the caller's JWT, so the
 * audience numbers are exactly what this user is allowed to see.
 */
export const CampaignAssistPanel = ({ campaignId }: { campaignId: string }) => {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{
    text: string;
    provider: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const suggest = async () => {
    setBusy(true);
    setError(null);
    const { data, error: fnError } = await invokeAi<{
      text: string;
      provider: string;
    }>({
      action: "campaign_assist",
      campaign: { campaign_id: campaignId },
      workspace_id: getActiveWorkspaceId(),
    });
    setBusy(false);
    if (fnError || !data) {
      setError(fnError ?? "Suggestions unavailable");
      return;
    }
    setResult(data);
  };

  return (
    <div className="flex flex-col gap-2 rounded-md border border-dashed border-border p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[12.5px] font-medium">AI suggestions</span>
        <button
          type="button"
          disabled={busy}
          onClick={suggest}
          className="flex items-center gap-1 text-[11.5px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline disabled:opacity-50"
        >
          {busy ? (
            <Loader2 className="size-3 animate-spin" />
          ) : (
            <Sparkles className="size-3" />
          )}
          {result ? "Regenerate" : "Suggest subject lines, audience and copy"}
        </button>
      </div>
      {error ? (
        <span className="text-[11.5px] text-destructive">{error}</span>
      ) : null}
      {result ? (
        <>
          <p className="whitespace-pre-wrap text-[12px] leading-relaxed text-foreground">
            {result.text}
          </p>
          <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
            {result.provider === "sandbox"
              ? "sandbox (no model called)"
              : `via ${result.provider}`}
          </span>
        </>
      ) : (
        <span className="text-[11.5px] text-muted-foreground">
          Ideas only. Nothing is changed or sent; you decide what to use.
        </span>
      )}
    </div>
  );
};
